import React from 'react';
import { useAuth } from '../contexts/AuthContext.tsx';
import { Permission } from '../types/auth';

interface PermissionGateProps {
  children: React.ReactNode;
  permissions?: Permission[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  showMessage?: boolean;
}

// 권한 에러 메시지 컴포넌트
const PermissionDeniedMessage: React.FC = () => (
  <div className="text-center py-4">
    <div className="inline-flex items-center justify-center w-12 h-12 mb-3 bg-red-100 rounded-full">
      <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
      </svg>
    </div>
    <p className="text-sm text-gray-600">
      이 기능을 사용할 권한이 없습니다.
    </p>
    <p className="text-xs text-gray-500 mt-1">
      필요한 권한이 있다고 생각되시면 관리자에게 문의하세요.
    </p>
  </div>
);

const PermissionGate: React.FC<PermissionGateProps> = ({ 
  children, 
  permissions = [], 
  requireAll = false,
  fallback = null,
  showMessage = false
}) => {
  const { user, hasAnyPermission, hasAllPermissions } = useAuth();

  // If no permissions specified, just check if user is authenticated
  if (permissions.length === 0) {
    return user ? <>{children}</> : null;
  }

  // Check permissions
  const hasPermission = requireAll 
    ? hasAllPermissions(permissions)
    : hasAnyPermission(permissions);

  if (!hasPermission) {
    if (fallback) {
      return <>{fallback}</>;
    }
    
    if (showMessage) {
      return <PermissionDeniedMessage />;
    }
    
    return null;
  }

  return <>{children}</>;
};

// Convenience components for common permission checks
export const AdminOnly: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => {
  const { isAdmin } = useAuth();
  return isAdmin ? <>{children}</> : <>{fallback || null}</>;
};

export const ManagerOnly: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => {
  const { isManager } = useAuth();
  return isManager ? <>{children}</> : <>{fallback || null}</>;
};

export const AdminOrManager: React.FC<{ children: React.ReactNode; fallback?: React.ReactNode }> = ({ 
  children, 
  fallback 
}) => {
  const { isAdmin, isManager } = useAuth();
  return (isAdmin || isManager) ? <>{children}</> : <>{fallback || null}</>;
};

// Feature-specific permission gates
export const CanEdit: React.FC<{ 
  feature: string; 
  children: React.ReactNode; 
  fallback?: React.ReactNode 
}> = ({ 
  feature, 
  children, 
  fallback 
}) => {
  const editPermission = `edit_${feature}` as Permission;
  return (
    <PermissionGate permissions={[editPermission]} fallback={fallback}>
      {children}
    </PermissionGate>
  );
};

export const CanView: React.FC<{ 
  feature: string; 
  children: React.ReactNode; 
  fallback?: React.ReactNode 
}> = ({ 
  feature, 
  children, 
  fallback 
}) => {
  const viewPermission = `view_${feature}` as Permission;
  return (
    <PermissionGate permissions={[viewPermission]} fallback={fallback}>
      {children}
    </PermissionGate>
  );
};

export default PermissionGate;